import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { readUiPrefs, writeUiPrefs } from '../store/settings-modal';

import enCommon from './locales/en/common.json';
import enAuth from './locales/en/auth.json';
import enSettings from './locales/en/settings.json';
import enDpa from './locales/en/dpa.json';
import enMenus from './locales/en/menus.json';
import enOps from './locales/en/ops.json';

import esCommon from './locales/es/common.json';
import esAuth from './locales/es/auth.json';
import esSettings from './locales/es/settings.json';
import esDpa from './locales/es/dpa.json';
import esMenus from './locales/es/menus.json';
import esOps from './locales/es/ops.json';

export type AppLocale = 'en' | 'es';

export const SUPPORTED_LOCALES: AppLocale[] = ['en', 'es'];
export const DEFAULT_LOCALE: AppLocale = 'en';

function normalizeLocale(raw: unknown): AppLocale {
  const s = String(raw || '').toLowerCase().slice(0, 2);
  return s === 'es' ? 'es' : 'en';
}

/** Resolve locale: prefs → navigator → en */
export function detectLocale(): AppLocale {
  try {
    const prefs = readUiPrefs();
    if (prefs?.locale) return normalizeLocale(prefs.locale);
  } catch {
    /* ignore */
  }
  try {
    if (typeof navigator !== 'undefined' && navigator.language) {
      return normalizeLocale(navigator.language);
    }
  } catch {
    /* ignore */
  }
  return DEFAULT_LOCALE;
}

const resources = {
  en: {
    common: enCommon,
    auth: enAuth,
    settings: enSettings,
    dpa: enDpa,
    menus: enMenus,
    ops: enOps,
  },
  es: {
    common: esCommon,
    auth: esAuth,
    settings: esSettings,
    dpa: esDpa,
    menus: esMenus,
    ops: esOps,
  },
};

let initialized = false;

export function initI18n(locale?: AppLocale) {
  const lng = locale ?? detectLocale();
  if (initialized) {
    if (i18n.language !== lng) void i18n.changeLanguage(lng);
    return i18n;
  }
  i18n.use(initReactI18next).init({
    resources,
    lng,
    fallbackLng: DEFAULT_LOCALE,
    defaultNS: 'common',
    ns: ['common', 'auth', 'settings', 'dpa', 'menus', 'ops'],
    interpolation: { escapeValue: false },
    returnNull: false,
  });
  initialized = true;
  return i18n;
}

/** Change language and persist preference. */
export function setAppLocale(locale: AppLocale) {
  const lng = normalizeLocale(locale);
  void i18n.changeLanguage(lng);
  try {
    writeUiPrefs({ locale: lng });
    window.dispatchEvent(new CustomEvent('nv:locale', { detail: { locale: lng } }));
  } catch {
    /* ignore */
  }
  try {
    document.documentElement.lang = lng;
  } catch {
    /* ignore */
  }
}

/** Non-React helper (bundle.jsx menus, etc.) */
export function tt(key: string, opts?: Record<string, unknown>): string {
  if (!initialized) initI18n();
  return String(i18n.t(key, opts));
}

// Eager init so tt() works early
initI18n();

export default i18n;
